import Model from '@ember-data/model';
import { computed } from '@ember/object';
import { equal, not, alias } from '@ember/object/computed';
import attr from 'ember-data/attr';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import { FormType, SharedFormModelInterface } from 'cio/utils/forms';
import { IdentifierKey } from 'cio/utils/identifiers';
import { modelApiAction } from 'cio/utils/model-api-actions';

// Transforms form name returned by FB Graph API, saved to CIO DB to a name without long "created on" date string:
// "Ami Co. form created on Thu Oct 22, 2020 10:04am" -> "Ami Co. form"
export function formatFacebookLeadAdsFormName(formName: string) {
  let formNameParts = formName.split('created on');

  // remove only last "created on" occurence and what follows after it
  // handles edge case when actual form name can contain "created on" string
  if (formNameParts.length > 1) {
    formNameParts.pop();
  }

  return formNameParts.join('created on').trim();
}

/*
  Form statuses returned by the API. Originally they were mapping 1-to-1 to display in the UI, but after product changes not anymore.
  Potential TODO: back-end could be re-factored to return better statuses, allow simplifying UI logic around accessing them.
*/
export enum FacebookLeadAdsFormStatus {
  Active = 'active',
  Inactive = 'inactive',
  Archived = 'archived',
}

export interface FacebookLeadAdsFormAttrMap {
  facebookQuestionKey: string;
  facebookQuestionLabel: string;
  facebookQuestionType: string;
  cioAttr: string;
}

export default class FacebookLeadAdsForm extends Model implements SharedFormModelInterface {
  @service store!: DS.Store;

  // always set to RecordArray with all segments currently in Ember Data store. Used to compute "formSegment":
  private allSegments = this.store.peekAll('segment');
  readonly formType = FormType.FacebookLeadAds;
  @alias('facebookFormId') formId!: string;
  @alias('formNameForDisplay') name!: string;

  @attr('string') facebookFormId!: string;
  @attr('string') facebookFormName!: string;
  @attr('string') status!: FacebookLeadAdsFormStatus;
  @attr('number') lastActivityAt!: number;

  @attr('camel-case', { defaultValue: () => [] }) attrMaps!: FacebookLeadAdsFormAttrMap[];
  @attr() extraAttrs!: Record<string, string>;

  updateAttrMapAtIndex(index: number, attrMap: FacebookLeadAdsFormAttrMap) {
    let attrMaps = [...this.attrMaps];
    attrMaps[index] = attrMap;
    this.attrMaps = attrMaps;
  }

  @equal('status', FacebookLeadAdsFormStatus.Archived) isArchived!: boolean;
  @equal('status', FacebookLeadAdsFormStatus.Inactive) canBeConnected!: boolean;
  @equal('status', FacebookLeadAdsFormStatus.Active) isConnected!: boolean;
  @not('isConnected') isDisconnected!: boolean;

  @computed('isConnected')
  get isOrWasConnected() {
    return (
      this.isConnected ||
      // a form segment means the form was connected at one stage:
      (this.isDisconnected && this.formSegment != null)
    );
  }

  get value() {
    return this.formId;
  }

  get label() {
    return this.name;
  }

  @computed('formId')
  get formIdLowercase() {
    return this.formId.toLowerCase();
  }

  get hasDuplicateFieldMapping() {
    let attrNames = this.attrMaps.map((f) => f.cioAttr.trim()).filter((attrName) => attrName); // filter out empty strings

    return new Set(attrNames).size !== attrNames.length;
  }

  get hasCioIdFieldMapping() {
    return this.attrMaps.toArray().some((f) => f.cioAttr === IdentifierKey.CioId);
  }

  @computed('facebookFormName')
  get formNameForDisplay() {
    if (!this.facebookFormName) {
      return '';
    }

    return formatFacebookLeadAdsFormName(this.facebookFormName);
  }

  // Depends on workspace segments being loaded into the Ember Data store:
  @computed('facebookFormId', 'allSegments.[]')
  get formSegment() {
    return this.allSegments.findBy('linkedFormId', this.facebookFormId);
  }

  updateAll = modelApiAction({
    path: 'update_all',
    type: 'POST',
    collection: true,
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'facebook-lead-ads-form': FacebookLeadAdsForm;
  }
}
